import { agentDisplayName, cyboNameOf } from "$lib/agent-display";
// One provider-neutral model for a message between two sessions/agents, so the
// stream renders the same card for Claude's native SendMessage, Cyborg's
// cyborg7_ask_agent on every provider (claude / codex / pi / opencode / ACP) and
// the peer's answer coming back. Pure: no Svelte, no store.

import type { StatusTone } from "$lib/color/status-tone.js";
import { launchedSubagentId, subagentLabelOfItem } from "./subagent-launch.js";
import {
  isEdgeFailure,
  joinEdgesToRows,
  type EdgeLane,
  type EdgeState,
  type MessageEdge,
} from "./message-edge.js";

export type PeerMessageStatus =
  | "sending"
  | "sent"
  | "delivered"
  | "replied"
  | "queued"
  | "held"
  | "unreachable"
  | "refused"
  | "declined"
  | "expired"
  | "dropped"
  | "failed";

export interface PeerMessage {
  direction: "out" | "in";
  target: { name: string; kind: "session" | "agent"; id?: string };
  /** The thread an ask opened ("threadId: …" in the tool result): its reply wakes the asker there. */
  threadId?: string;
  /** The ask message ("askId: …" in the tool result): the relay's refusal names THIS, never the thread. */
  askId?: string;
  /** The handoff chain a prompt_session opened ("chain <id>" in its result): the reply row carries the same id. */
  chainId?: string;
  /** The relay's message edge of this send ("edge <id>)" in the tool result): the exact join to `edge`. */
  edgeId?: string;
  /** The target is a subagent this session launched (not a Cyborg session or a terminal). */
  subagent?: boolean;
  /** That subagent's id in the provider-subagent store (its Task call id): what the row opens. */
  subagentId?: string;
  /** When the card first arrived (ms), from the stream state's side table. */
  at?: number;
  summary?: string;
  body: string;
  status: PeerMessageStatus;
  reason?: string;
  reply?: { from: string; body: string; at?: string };
  /** The Cyborg lane the tool sends on; absent for Claude's native SendMessage (no server edge exists for it). */
  lane?: EdgeLane;
  /** The relay's record of this message. When present it decides the row; the tool-result text is the fallback. */
  edge?: MessageEdge;
  /** Some of a passed-on message's files stayed behind: "10 of 14 files went". */
  filesNote?: string;
}

// The ONE table of wording the daemon/provider uses. A phrase moves here, nowhere else.
// First match wins. `sent` is deliberately not "delivered": Claude has no delivery
// signal, success only means queued at the peer; the joined reply is what proves receipt.
const RESULT_PHRASES: ReadonlyArray<[RegExp, PeerMessageStatus]> = [
  [/held for the recipient user's approval/i, "queued"],
  [/not accepting cross-session messages|No agent named|question not delivered/i, "unreachable"],
  // cyborg7_ask_agent refusals (askRefusal in cyborg7-mcp-tools.ts); also isError on the wire.
  [
    /^Error: (only an agent can ask|no agent '|'.*' names more than one agent|you cannot ask yourself|thread '.*' is not in a channel)/im,
    "failed",
  ],
  [/"success"\s*:\s*false/i, "failed"],
];

// Claude's later `[Cross-session delivery notice] …` turn settles the card of the message it is about
// (the daemon's peer-transcript-follower appends it to the SENDER's timeline). First match wins.
export const DELIVERY_NOTICE_PREFIX = "[Cross-session delivery notice]";
const NOTICE_PHRASES: ReadonlyArray<[RegExp, PeerMessageStatus]> = [
  // Real CLI wording (measured): "was held by that session (recipient: uds:…; msg_id: …)", "N of your messages … were refused (…)".
  [/held for the recipient user's approval|held by that session/i, "held"],
  [
    /(?:was|were) refused\b|refused: that session is not accepting cross-session messages/i,
    "refused",
  ],
  [/denied by the recipient user|(?:was|were) denied\b/i, "declined"],
  [/not approved before expiry/i, "expired"],
  [/approved and released to that session/i, "sent"],
  [/dropped at that session's inbox and NOT delivered/i, "dropped"],
];

/** What a peer hold that dropped by expiry reads, on its card and its alert row. */
export const PEER_HOLD_EXPIRED_TEXT = "Expired — not delivered";

/** The status a notice's wording names, or undefined when the parser does not recognise it. */
export const noticeStatusOf = (text: string): PeerMessageStatus | undefined =>
  firstMatch(NOTICE_PHRASES, text);

export const PEER_STATUS_LABEL: Record<PeerMessageStatus, string> = {
  sending: "sending",
  sent: "sent",
  delivered: "delivered",
  replied: "replied",
  queued: "queued",
  held: "held",
  unreachable: "not reachable",
  refused: "not delivered",
  declined: "declined",
  expired: "not delivered",
  dropped: "not delivered",
  failed: "failed",
};

export const PEER_STATUS_TONE: Record<PeerMessageStatus, StatusTone> = {
  sending: "attention",
  sent: "ready",
  delivered: "ready",
  replied: "ready",
  queued: "attention",
  held: "attention",
  unreachable: "failed",
  refused: "failed",
  declined: "failed",
  expired: "failed",
  dropped: "failed",
  failed: "failed",
};

// What a non-delivery says when the provider's own text is empty.
export const PEER_STATUS_NOTE: Partial<Record<PeerMessageStatus, { reason: string }>> = {
  unreachable: { reason: "That session is not accepting cross-session messages. Not delivered." },
  failed: { reason: "The message was not sent." },
};

/** The row's status pill: words per the A2A row design. `tone` picks only the TEXT colour (muted unless it needs a person). */
export const PEER_PILL: Record<
  PeerMessageStatus,
  { label: string; tone: "muted" | "amber" | "red" }
> = {
  sending: { label: "Sending…", tone: "muted" },
  sent: { label: "Sent", tone: "muted" },
  delivered: { label: "Delivered", tone: "muted" },
  replied: { label: "Replied", tone: "muted" },
  queued: { label: "Waiting approval", tone: "amber" },
  held: { label: "Waiting approval", tone: "amber" },
  unreachable: { label: "Not delivered", tone: "red" },
  refused: { label: "Not delivered", tone: "red" },
  declined: { label: "Declined", tone: "red" },
  expired: { label: "Not delivered", tone: "red" },
  dropped: { label: "Not delivered", tone: "red" },
  failed: { label: "Not delivered", tone: "red" },
};

export function isPeerProblem(status: PeerMessageStatus): boolean {
  return PEER_STATUS_TONE[status] === "failed";
}

// ── tool call → PeerMessage ──────────────────────────────────────────────────

export interface PeerToolEntry {
  name: string;
  status: string;
  detail?: unknown;
  error?: unknown;
}

// Matched ANYWHERE in the name so every provider's spelling hits: mcp__cyborg7__cyborg7_ask_agent,
// cyborg7.cyborg7_ask_agent, cyborg7_cyborg7_ask_agent, and an ACP title that carries the id.
// Claude's native SendMessage is exact. cyborg7_send_message / reply_in_thread post to a
// channel or a person, never to a peer, so they are not here.
const PEER_TOOLS: ReadonlyArray<[RegExp, "session" | "agent", EdgeLane?]> = [
  [/cyborg7_ask_agent/, "agent", "ask_agent"],
  [/^SendMessage$/, "session"],
  // Drives one of the asking human's OTHER sessions; its reply lands in THIS session carrying the chain id.
  [/cyborg7_prompt_session/, "session", "prompt_session"],
  // A message to a session or an agent (`to`, `text`). Not cyborg7_send_message: that posts to a channel or a person.
  [/cyborg7_message\b/, "session", "session_message"],
];

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);

// Flattens a tool result (string, MCP content blocks, object) to the text a person would read.
function textOf(v: unknown): string {
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(textOf).filter(Boolean).join("\n");
  const rec = asRecord(v);
  if (!rec) return String(v ?? "");
  // `output`: Claude's MCP tool result is `{ output: "<text>" }` (the stored row of an ask). Without it the object
  // is JSON-stringified, its newlines escape, and the line-anchored `threadId:` / `askId:` reads below find nothing.
  const inner = rec.text ?? rec.message ?? rec.content ?? rec.output ?? rec.error;
  return inner !== undefined ? textOf(inner) : JSON.stringify(rec);
}

// A result stored as a JSON string of MCP blocks (`[{"type":"text","text":"…"}]`): its newlines are escaped, so the
// line-anchored `threadId:` / `Asked … in #` reads would never match. Plain text passes through untouched.
function unwrapJsonText(s: string): string {
  if (!/^\s*[[{]/.test(s)) return s;
  try {
    return textOf(JSON.parse(s));
  } catch {
    // intentional: not JSON after all (a bracketed plain line) — read it as text
    return s;
  }
}

function firstMatch(
  table: ReadonlyArray<[RegExp, PeerMessageStatus]>,
  text: string,
): PeerMessageStatus | undefined {
  for (const [re, status] of table) if (re.test(text)) return status;
  return undefined;
}

function statusOf(status: string | undefined): PeerMessageStatus {
  if (status === "running") return "sending";
  return status === "failed" || status === "canceled" ? "failed" : "sent";
}

function peerCallOf(entry: PeerToolEntry) {
  const [, kind, lane] = PEER_TOOLS.find(([re]) => re.test(entry.name)) ?? [];
  const detail = asRecord(entry.detail);
  const input = kind && detail?.type === "unknown" ? asRecord(detail.input) : undefined;
  const asked =
    input && (str(input.agent) ?? str(input.to) ?? str(input.recipient) ?? str(input.sessionId));
  if (!kind || !input || !asked) return undefined;
  return { kind, lane, input, asked, output: unwrapJsonText(textOf(detail?.output)) };
}

// Any "Error…" (incl. "Error (not_found): …") a cyborg7 tool returns is a failure (askRefusal / mcpError), not a green "sent".
// "delivered" only for prompt_session's own "Delivered to session" line. An ask and Claude's SendMessage have no
// delivery signal (the CLI says nothing of the kind), so they stay "sent" until a reply joins.
// Not anchored: the result can arrive JSON-wrapped (`[{"type":"text","text":"Delivered to session …"}]`).
const DELIVERED_RE = /\bDelivered to session\b/;

function callStatus(
  name: string,
  output: string,
  result: string,
  status: string,
): PeerMessageStatus {
  const cyborgTool = /cyborg7_/.test(name);
  const phrase =
    firstMatch(RESULT_PHRASES, result) ??
    (cyborgTool && /^\s*Error\b/.test(output) ? ("failed" as const) : undefined);
  // A `canceled` card that holds its result was sent: the client's turn-end sweep (resolveOrphanedToolCards) can
  // cancel a call mid-flight and that cancel is sticky over the later `completed` row that carries the result.
  const base = phrase ?? (status === "canceled" && output.trim() ? "sent" : statusOf(status));
  return base === "sent" && /cyborg7_prompt_session/.test(name) && DELIVERED_RE.test(output)
    ? "delivered"
    : base;
}

// An ask's result names its thread and its own message ("threadId: …", "askId: …").
function askIdsOf(output: string): { threadId?: string; askId?: string } {
  const threadId = /^threadId:\s*(\S+)/m.exec(output)?.[1];
  const askId = /^askId:\s*(\S+)/m.exec(output)?.[1];
  return { ...(threadId && { threadId }), ...(askId && { askId }) };
}

// The head of a message the relay took (server session-message-tool.ts sessionMessageSentText and
// session-handoff-chains.ts): "<Delivered to|Recorded for|Already sent to> <session|agent> <id> (…".
const SENT_HEAD_RE =
  /\b(?:Delivered to|Recorded for|Already sent to) (session|agent) ([0-9a-zA-Z_-]+)/;
const sentTarget = (noun: string, id: string): { id: string; kind?: "agent" } =>
  noun === "agent" ? { id, kind: "agent" } : { id };

// The relay's edge id as the daemon tool writes it: "(chain <id>, hop n/m, edge <id>)" on a delivery,
// " (edge <id>)" closing a refusal. The LAST one: a refusal quotes the caller's own reference before it.
const EDGE_ID_RE = /\bedge ([0-9A-Za-z][0-9A-Za-z_-]{3,})\)/g;
function edgeIdOf(output: string): string | undefined {
  return [...output.matchAll(EDGE_ID_RE)].pop()?.[1];
}

// A row the relay's ledger lane stamped (hand-off chain or "edge <id>)" token): joined by its edge, not by turn.
const hasEdgeToken = (e: PeerUserEntry): boolean =>
  !!e.promptAuthor?.chainId || (typeof e.content === "string" && !!edgeIdOf(e.content));

// A native message (mirror row, no edge token) that arrives after a later human turn is drawn where it arrived
// ("From"), never inside an old card: it may join only a card sent after that turn. Edge rows keep their exact join.
function turnGate(e: PeerUserEntry, index: Map<string, number>, lastHuman: number) {
  if (e.peerRelay?.kind !== "mirror" || hasEdgeToken(e)) return () => true;
  return (id: string) => (index.get(id) ?? -1) > lastHuman;
}

export function peerMessageFromToolCall(entry: PeerToolEntry): PeerMessage | undefined {
  const call = peerCallOf(entry);
  if (!call) return undefined;
  const { kind, lane, input, asked, output } = call;
  // `agent` may be an id: the tool's own "Asked <name> in #…" line is the resolved name.
  const name = (kind === "agent" && /^Asked (.+) in #/m.exec(output)?.[1]) || asked;
  const result = [output, textOf(entry.error)].filter(Boolean).join("\n").trim();
  const status = callStatus(entry.name, output, result, entry.status);
  const msg: PeerMessage = {
    direction: "out",
    target: { name, kind },
    body: str(input.text) ?? str(input.message) ?? str(input.prompt) ?? "",
    status,
    ...(lane && { lane }),
  };
  const handedTo = SENT_HEAD_RE.exec(output);
  if (handedTo) Object.assign(msg.target, sentTarget(handedTo[1], handedTo[2]));
  const chainId = /\(chain ([^,\s)]+)/.exec(output)?.[1];
  if (chainId) msg.chainId = chainId;
  // A refusal is the call's ERROR, not its output (Claude stores `output: null` on a failed MCP call).
  const edgeId = lane && edgeIdOf(result);
  if (edgeId) msg.edgeId = edgeId;
  if (kind === "agent") Object.assign(msg, askIdsOf(output));
  const summary = str(input.summary);
  if (summary) msg.summary = summary;
  if (isPeerProblem(status)) msg.reason = result.slice(0, 400) || undefined;
  const files = FILES_OF_RE.exec(output);
  if (files) msg.filesNote = `${files[1]} of ${files[2]} files went`;
  return msg;
}

// server session-message-tool.ts filesSentence, when some files stayed behind: "10 of 14 files went with it".
const FILES_OF_RE = /\b(\d+) of (\d+) files went with it\b/;

// ── user turn → incoming peer ────────────────────────────────────────────────

const WRAPPER_RE = /^\s*<cross-session-message\b([^>]*)>([\s\S]*?)<\/cross-session-message>\s*$/;

function attr(attrs: string, key: string): string | undefined {
  return new RegExp(`\\b${key}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`)
    .exec(attrs)
    ?.slice(1)
    .find(Boolean);
}

export function peerMessageFromUserTurn(
  text: string,
): { peer: { name: string; address?: string }; body: string } | undefined {
  const m = WRAPPER_RE.exec(text);
  if (!m) return undefined;
  const address = attr(m[1], "from");
  const name = attr(m[1], "from-name") ?? address;
  if (!name) return undefined;
  return { peer: { name, address }, body: m[2].trim() };
}

// The origin stamp ONLY the daemon's peer-transcript-follower writes on a row it imported. A
// human-routed prompt is built without it, so it is the one thing a typed
// `<cross-session-message>` can never carry.
export interface PeerRelayMark {
  kind: "mirror" | "notice";
  /** When the peer message was originally received (ISO), stamped by the daemon follower: it can be imported late. */
  at?: string;
}

export interface PeerUserEntry {
  id: string;
  kind: string;
  content?: string;
  ts?: number;
  messageId?: string;
  clientMessageId?: string;
  /** `chainId` etc.: the handoff stamp on a prompt_session row (and on its reply); absent on an older server. */
  promptAuthor?: {
    kind: string;
    name?: string;
    chainId?: string;
    hop?: number;
    fromSessionId?: string;
    fromCyboId?: string;
  };
  peerRelay?: PeerRelayMark;
  /** Thread an agent-authored wake came from (message-router buildUserTurnItem). */
  wakeThreadId?: string;
  /** Who answered: set by the daemon ONLY on the wake the relay settled as an ask's answer. */
  askReplyFrom?: { cyboId: string; name: string; refused?: boolean };
}

export interface IncomingPeer {
  /** Absent on an ask reply: the relay wake carries no author name. */
  name?: string;
  threadId?: string;
  /** Set only on the settled answer to an ask: the one row an ask card may join. */
  askReplyFrom?: { cyboId: string; name: string; refused?: boolean };
  address?: string;
  chainId?: string;
  /** The sending session's full id on a prompt_session row: the identity avatar/name/machine resolve from. */
  fromSessionId?: string;
  /** The agent whose session sent it (the daemon's handoff stamp): the row is from the Agent. */
  fromCyboId?: string;
  /** The edge this row answers: the relay's automatic reply is stored under the id `reply:<edgeId>`. */
  inReplyToEdge?: string;
  kind: "session" | "agent";
  body: string;
}

// The relay's own prompt id for the answer it forwards back (server message-edge.ts replyPromptId).
const REPLY_ID_RE = /^reply:(.+)$/;

// A user turn another session/agent wrote: the wrapper (Claude native, ONLY on a row the
// peer relay stamped: text a person types is never re-attributed) or the daemon's
// promptAuthor stamp (Cyborg ask replies, every provider).
export function incomingPeerOf(entry: PeerUserEntry): IncomingPeer | undefined {
  if (entry.kind !== "user_message" || typeof entry.content !== "string") return undefined;
  if (entry.peerRelay && entry.clientMessageId) return undefined;
  if (entry.peerRelay?.kind === "notice") return undefined;
  if (entry.peerRelay?.kind === "mirror" && entry.promptAuthor?.kind === "agent") {
    const wrapped = peerMessageFromUserTurn(entry.content);
    if (wrapped) return { ...wrapped.peer, kind: "session", body: wrapped.body };
  }
  if (entry.promptAuthor?.kind === "agent") {
    return {
      name: entry.promptAuthor.name,
      ...(entry.promptAuthor.chainId ? { chainId: entry.promptAuthor.chainId } : {}),
      ...(entry.promptAuthor.fromSessionId
        ? { fromSessionId: entry.promptAuthor.fromSessionId }
        : {}),
      ...(entry.promptAuthor.fromCyboId ? { fromCyboId: entry.promptAuthor.fromCyboId } : {}),
      ...(entry.wakeThreadId ? { threadId: entry.wakeThreadId } : {}),
      ...(entry.askReplyFrom ? { askReplyFrom: entry.askReplyFrom } : {}),
      ...replyEdgeOf(entry),
      // A hand-off row comes from a session (the sender's own, by fromSessionId), not an ask target.
      kind: entry.promptAuthor.chainId ? "session" : "agent",
      body: entry.content,
    };
  }
  return undefined;
}

// The daemon stores the forward's prompt id as the row's messageId (and as its client id).
function replyEdgeOf(entry: PeerUserEntry): { inReplyToEdge?: string } {
  const id =
    REPLY_ID_RE.exec(entry.messageId ?? "") ?? REPLY_ID_RE.exec(entry.clientMessageId ?? "");
  return id?.[1] ? { inReplyToEdge: id[1] } : {};
}

/** Only the mirrored SendMessage draws as a bubble; every other agent-authored row renders as before. */
export function isPeerBubbleRow(entry: PeerUserEntry): boolean {
  return (
    (!!entry.peerRelay || !!entry.promptAuthor?.chainId) && incomingPeerOf(entry) !== undefined
  );
}

// ── joining a timeline ───────────────────────────────────────────────────────

export interface PeerStreamEntry extends PeerUserEntry {
  callId?: string;
  name?: string;
  status?: string;
  detail?: unknown;
  /** The subagent id the launch result named; kept by the stream state after the card's detail turns sub_agent. */
  subagentId?: string;
  /** That launch's description; later sub_agent rows drop it. */
  subagentLabel?: string;
  error?: unknown;
}

// A peer listing can tag a name with its ref ("worker [3fa9c1]"): the name is what the other side calls itself.
const bareName = (n: string): string =>
  n
    .replace(/\s*\[[0-9a-f]{6,12}\]$/, "")
    .trim()
    .toLowerCase();
const sameName = (a: string, b: string): boolean => bareName(a) === bareName(b);

export interface PeerJoin {
  /** callId → the card to draw. */
  cards: Map<string, PeerMessage>;
  /** user_message ids drawn inside a card (a reply). */
  consumed: Set<string>;
  /** Answers a later answer replaced in their card (latest wins): drawn as rows again, by design. */
  displaced: Set<string>;
  /** Entry index each card sits at, for the pending/strip views. */
  index: Map<string, number>;
}

/** How a raw id becomes a name. Both are optional: without them the id shows, as before. */
export interface PeerNameResolver {
  /** A subagent's display title (provider subagent store), by the Task call id that spawned it. */
  subagentLabel?: (callId: string) => string | undefined;
  /** A cybo id / agent session id -> its cybo name or alias/peerName. */
  displayName?: (raw: string) => string | undefined;
  /** Why the relay refused an ask ("requires Pro"), by the ask message id. */
  refusalOf?: (askId: string) => string | undefined;
  /** When the card with this call id first arrived (stream-state side table, not part of the entry). */
  cardTs?: (callId: string) => number | undefined;
  /** The relay's message edges this viewer holds, and the ids the viewed session sends as (agent id, cybo id). */
  edges?: { list: readonly MessageEdge[]; selfIds: ReadonlySet<string> };
}

const EDGE_FAILURE_STATUS: Partial<Record<EdgeState, PeerMessageStatus>> = {
  refused: "refused",
  failed: "failed",
  expired: "expired",
};

// The edge decides the row: the relay wrote it from real signals, the tool-result text is only what the
// caller was told. A reply row already joined under the card stays "replied" (the row is the proof).
function applyEdge(card: PeerMessage, edge: MessageEdge, resolve?: PeerNameResolver): void {
  card.edge = edge;
  const failure = EDGE_FAILURE_STATUS[edge.state];
  if (failure) card.status = failure;
  else if (edge.state === "replied" || card.reply) card.status = "replied";
  else if (card.status !== "sending") card.status = "sent";
  if (!isEdgeFailure(edge.state)) card.reason = undefined;
  // A node this viewer has no row for still shows its name: the one the relay resolved.
  const known = resolve?.displayName?.(edge.to.id);
  card.target = {
    ...card.target,
    // The relay says what the other node is: a message can name an agent the tool call typed as a session.
    ...(edge.to.kind === "agent" ? { kind: "agent" as const } : {}),
    id: card.target.id ?? edge.to.id,
    // The relay knows an own subagent only by its raw id: the resolved name stands.
    name: card.subagent ? card.target.name : (known ?? (edge.to.name || card.target.name)),
  };
}

// Claude's agent id for a subagent ("a" + 16 hex): what SendMessage / cyborg7_message address it by.
const CLAUDE_SUBAGENT_ID_RE = /^a[0-9a-f]{16}$/;

interface LaunchedSubagent {
  name?: string;
  callId: string;
}

// subagent id -> the name to show (the live store's label for the Task call, else the Task's own input) + its call id.
function subagentNames(
  entries: readonly PeerStreamEntry[],
  resolve?: PeerNameResolver,
): Map<string, LaunchedSubagent> {
  const names = new Map<string, LaunchedSubagent>();
  for (const e of entries) {
    if (e.kind !== "tool_call" || !e.callId || !e.name || !SUBAGENT_TOOLS.has(e.name)) continue;
    const detail = asRecord(e.detail);
    const id = e.subagentId ?? launchedSubagentId(detail);
    if (!id) continue;
    // The entry's first-seen label (later sub_agent rows drop it), else what the row itself carries.
    const label = resolve?.subagentLabel?.(e.callId) ?? e.subagentLabel ?? subagentLabelOfItem(e);
    names.set(id, { name: label, callId: e.callId });
  }
  return names;
}

const SUBAGENT_TOOLS: ReadonlySet<string> = new Set(["Task", "Agent", "Workflow", "Sub-agent"]);

// Never show a raw id: a subagent, cybo or session id shows its name; the id rides along as target.id.
function withDisplayName(
  msg: PeerMessage,
  names: { subagents: Map<string, LaunchedSubagent>; addrs: Map<string, string> },
  resolve?: PeerNameResolver,
): PeerMessage {
  const raw = msg.target.name;
  // An own subagent, however the tool typed it: the shared label, else "Subagent", never the id.
  const launched = names.subagents.get(raw);
  if (launched || CLAUDE_SUBAGENT_ID_RE.test(raw)) {
    return {
      ...msg,
      target: { ...msg.target, name: launched?.name ?? "Subagent", id: msg.target.id ?? raw },
      subagent: true,
      ...(launched ? { subagentId: launched.callId } : {}),
    };
  }
  const session = msg.target.kind === "session";
  const name =
    (session ? names.addrs.get(raw) : undefined) ??
    resolve?.displayName?.(raw) ??
    // prompt_session: the model passed an alias/prefix, the result named the full session id.
    (msg.target.id ? resolve?.displayName?.(msg.target.id) : undefined);
  return name && name !== raw
    ? { ...msg, target: { ...msg.target, name, id: msg.target.id ?? raw } }
    : msg;
}

// A session id or a start of one, an agent id, a default-conversation id: what a caller passes when it has no name.
const RAW_ID_RE = /^(?:[0-9a-f]{8}(?:-?[0-9a-f-]{0,28})?|cybo_[0-9a-z_-]+|dmdef-[0-9a-z_-]+)$/i;
/** The name shown for a Cyborg target nothing resolved: the id the caller typed stays in `target.id`. */
export const UNRESOLVED_TARGET_NAME = {
  session: "Unknown session",
  agent: "Unknown agent",
} as const;

// A refused Cyborg message whose edge names no target (or that has no edge) shows what the caller typed.
// When that is an id nothing here resolves (an archived or unknown session), the row never shows it.
function hideUnresolvedId(card: PeerMessage): void {
  const raw = card.target.name.trim();
  if (!card.lane || card.edge?.to.name || !isPeerProblem(card.status) || !RAW_ID_RE.test(raw))
    return;
  card.target = {
    ...card.target,
    id: card.target.id ?? raw,
    name: UNRESOLVED_TARGET_NAME[card.target.kind],
  };
}

// address -> the from-name last seen on a daemon-stamped inbound wrapper with that same `from=`.
export function addressNames(entries: readonly PeerStreamEntry[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const e of entries) {
    const peer = incomingPeerOf(e);
    if (peer?.address && peer.name && peer.name !== peer.address)
      names.set(peer.address, peer.name);
  }
  return names;
}

/** A notice row's individual notices, each with its prefix again. */
export const noticeParts = (text: string): string[] =>
  text
    .split(DELIVERY_NOTICE_PREFIX)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => `${DELIVERY_NOTICE_PREFIX} ${t}`);

/** The daemon's own notice row: never an authored chat message, even when no card takes it. */
export const isNoticeRow = (e: PeerUserEntry): boolean =>
  e.kind === "user_message" &&
  e.peerRelay?.kind === "notice" &&
  typeof e.content === "string" &&
  e.content.startsWith(DELIVERY_NOTICE_PREFIX);

// "(recipient: uds:/tmp/cc-socks/1.sock; msg_id: a, b)": the address, then the ids of the messages the notice covers.
const NOTICE_RECIPIENTS_RE = /\s*\(recipients?:\s*([^;)]*)(?:;\s*msg_ids?:\s*([^)]*))?\)/i;

/** The notice's address(es) and how many messages it covers ("2 of your messages", or its msg_id count). */
function parseNoticeTargets(text: string): { recipients: string[]; count: number } {
  const m = NOTICE_RECIPIENTS_RE.exec(text);
  const recipients =
    m?.[1]
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean) ?? [];
  const ids =
    m?.[2]
      ?.split(",")
      .map((r) => r.trim())
      .filter(Boolean).length ?? 0;
  return { recipients, count: ids || Number(/(\d+) of your messages/.exec(text)?.[1] ?? 1) };
}

/** A notice's text with every socket path and message id removed: the one-line status a person can read. */
export function scrubNotice(text: string): string {
  return text
    .slice(DELIVERY_NOTICE_PREFIX.length)
    .replace(NOTICE_RECIPIENTS_RE, "")
    .replace(/\S*uds:\S*/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const NOTICE_WORDS: Partial<Record<PeerMessageStatus, string>> = {
  held: "waiting for approval",
  refused: "not delivered",
  declined: "declined",
  expired: "expired, not delivered",
  sent: "approved and released",
  dropped: "not delivered",
};

/** The muted one-liner for a notice no card took: "Delivery update for <name>: <status>". No path, no id. */
export function noticeSummary(
  text: string,
  nameOf: (address: string) => string | undefined,
): string {
  const status = firstMatch(NOTICE_PHRASES, text);
  const { recipients } = parseNoticeTargets(text);
  const names = recipients.map((r) => nameOf(r)).filter((n): n is string => !!n);
  const who = names.length > 0 ? names.join(", ") : "another session";
  return `Delivery update for ${who}: ${(status && NOTICE_WORDS[status]) || "see the session"}`;
}

/** Every notice in a notice row, one line each, joined: what an unsettled notice row shows instead of its raw text. */
export function noticeRowSummary(
  content: string,
  nameOf: (address: string) => string | undefined,
): string {
  return noticeParts(content)
    .map((p) => noticeSummary(p, nameOf))
    .join(" · ");
}

// The daemon's own notice row (peerRelay is never on a human's) names its recipient(s) by socket address. It settles
// the card addressed to each, by that address or by the name a wrapper from it carried. First contact (that address never
// wrote to us) settles a card only when exactly ONE card could be it (`awaiting`: sent/queued, current turn, no notice yet,
// not a subagent); with several it settles nothing: never a guess.
// Returns whether it settled a card: a notice that settled nothing stays a visible row.
function settleFromNotice(
  text: string,
  cards: Map<string, PeerMessage>,
  order: readonly string[],
  addrs: Map<string, string>,
  bound: Map<string, PeerMessage[]>,
  noticed: Set<PeerMessage>,
  awaiting: readonly PeerMessage[],
): boolean {
  const status = firstMatch(NOTICE_PHRASES, text);
  if (!status) return false;
  const { recipients, count } = parseNoticeTargets(text);
  // The socket path and msg_id are plumbing, not a reason a person can use.
  const reason = status === "sent" ? undefined : scrubNotice(text);
  const sessionCards = order
    .map((id) => cards.get(id))
    .filter((c): c is PeerMessage => c?.target.kind === "session");
  const settle = (card: PeerMessage) => {
    noticed.add(card);
    card.status = status;
    card.reason = reason;
  };
  // One notice can cover N messages to ONE address ("2 of your messages … were refused"): settle N cards.
  const times = recipients.length === 1 ? Math.max(1, count) : 1;
  let settled = false;
  for (const to of recipients) {
    const named = addrs.get(to);
    for (let k = 0; k < times; k++) {
      // bound[to]: held cards of this address, oldest first. A release/denial/expiry settles the oldest, in any turn.
      const queue = (bound.get(to) ?? []).filter((c) => !c.reply);
      const open = (c: PeerMessage) => !(noticed.has(c) && c.status !== "held");
      // A card with a joined reply is delivered: no later notice may take it.
      const matches = (c: PeerMessage) =>
        !c.reply &&
        (c.target.id === to ||
          c.target.name === to ||
          (!!named &&
            (sameName(c.target.name, named) || (!!c.target.id && sameName(c.target.id, named)))));
      const card =
        (status === "held" ? undefined : queue[0]) ??
        sessionCards.findLast((c) => matches(c) && (status === "held" ? !noticed.has(c) : open(c)));
      const first = awaiting.find((c) => !noticed.has(c));
      const target = card ?? (!named && awaiting.length === 1 ? first : undefined);
      if (!target) break;
      settle(target);
      const rest = queue.filter((c) => c !== target);
      bound.set(to, status === "held" ? [...rest, target] : rest);
      settled = true;
    }
  }
  return settled;
}

// The recipient's CLI expires a hold (dialogExpiry 60s | 5m | 10m) and reports it to the sender's socket, which a restart
// replaces: that notice is lost. Past the longest preset no native hold is still waiting; a reply or release never flips.
const NATIVE_HOLD_MAX_WAIT_MS = 600_000;
function expireLostHold(card: PeerMessage, now: number): void {
  const waiting = card.status === "held" || card.status === "queued";
  if (!waiting || card.lane || card.reply || card.at === undefined) return;
  if (now - card.at < NATIVE_HOLD_MAX_WAIT_MS) return;
  card.status = "expired";
  card.reason = "Your message to another session was not approved before expiry. Not delivered.";
}

// A card is eligible for a row only if the row's own receive time (peerRelay.at) is not before the card: a late-imported
// old reply or notice must not settle today's in-flight card.
const cardNotAfter =
  (e: PeerStreamEntry, cardTs: (id: string) => number | undefined) => (id: string) => {
    const at = Date.parse(e.peerRelay?.at ?? "");
    const ts = cardTs(id);
    return Number.isNaN(at) || ts === undefined || at >= ts;
  };

// The reply a joined peer row becomes; `at` is when it was RECEIVED (peerRelay.at), not when it was imported.
const replyOf = (
  peer: IncomingPeer,
  e: PeerStreamEntry,
  card: PeerMessage,
  resolve?: PeerNameResolver,
): NonNullable<PeerMessage["reply"]> => ({
  from:
    peer.askReplyFrom?.name ||
    // The answering session as this viewer names it (alias, shared name), else as it signed.
    (peer.inReplyToEdge && peer.fromSessionId && resolve?.displayName?.(peer.fromSessionId)) ||
    (peer.name && (resolve?.displayName?.(peer.name) ?? peer.name)) ||
    card.target.name,
  body: peer.body,
  at: e.peerRelay?.at ?? (e.ts ? new Date(e.ts).toISOString() : undefined),
});

// Whether an unanswered card is the one this inbound peer row answers.
function replyTargets(c: PeerMessage, peer: IncomingPeer): boolean {
  // The relay's refusal wake tells the asker the ask was refused: it is not the target's reply (the card reads Not delivered).
  if (peer.askReplyFrom?.refused) return false;
  // The relay's automatic reply names the edge it answers: that send and no other, whoever served it
  // (an agent target answers from one of its sessions). A send on another page takes nothing.
  if (peer.inReplyToEdge) return c.edgeId === peer.inReplyToEdge;
  // The chain id is reused every hop (A to B to C to A): the replying session must also be the one the card sent to.
  if (peer.chainId && c.chainId) {
    return (
      peer.chainId === c.chainId && (!peer.fromSessionId || peer.fromSessionId === c.target.id)
    );
  }
  const from = peer.askReplyFrom;
  if (peer.threadId) {
    return (
      !!from &&
      !!c.threadId &&
      c.threadId === peer.threadId &&
      // target.id is the cybo id, or the raw name the model passed when the resolver renamed the card.
      (c.target.id === from.cyboId || sameName(c.target.id ?? c.target.name, from.name))
    );
  }
  const byAddress =
    !!peer.address && (c.target.name === peer.address || c.target.id === peer.address);
  return (
    byAddress ||
    (!!peer.name &&
      (sameName(c.target.name, peer.name) || (!!c.target.id && sameName(c.target.id, peer.name))))
  );
}

interface ReplyJoinCtx {
  cards: Map<string, PeerMessage>;
  order: string[];
  notBefore: (id: string) => boolean;
  replyEntry: Map<PeerMessage, string>;
  consumed: Set<string>;
  displaced: Set<string>;
  resolve?: PeerNameResolver;
}

// An ask reply joins only when it is the answer the server settled (askReplyFrom) in the thread the ask opened, from
// the agent the card asked: another agent posting in that thread is a normal row. A mirrored/named row joins the
// latest unanswered card to that name. Returns whether the row was consumed.
function joinReply(e: PeerStreamEntry, peer: IncomingPeer, ctx: ReplyJoinCtx): boolean {
  const { cards, order, notBefore, replyEntry, consumed, displaced, resolve } = ctx;
  // The settled answer carries no ask id on the wire, only its thread and responder. Two asks to the same
  // agent in one thread therefore each take their own answer: an unanswered card is filled first, and an
  // earlier answer is replaced (latest wins) only when every matching card already has one.
  const matches = (id: string, openOnly: boolean) => {
    const c = cards.get(id);
    return !!c && (!openOnly || !c.reply) && notBefore(id) && replyTargets(c, peer);
  };
  const target = peer.askReplyFrom
    ? (order.find((id) => matches(id, true)) ?? order.findLast((id) => matches(id, false)))
    : order.findLast((id) => matches(id, true));
  const card = target ? cards.get(target) : undefined;
  if (!card) return false;
  // PeerBubble nests ONE reply: the earlier answer goes back to a normal row instead of vanishing.
  const earlier = replyEntry.get(card);
  if (earlier) {
    consumed.delete(earlier);
    displaced.add(earlier);
  }
  replyEntry.set(card, e.id);
  card.reply = replyOf(peer, e, card, resolve);
  // A real refusal/failure stays visible: a later mirror from the same peer may answer something else.
  if (!isPeerProblem(card.status)) card.status = "replied";
  return true;
}

// The relay refused the ask (no Pro, not a member …): it never reached the agent.
export function joinPeerMessages(
  entries: readonly PeerStreamEntry[],
  resolve?: PeerNameResolver,
): PeerJoin {
  const names = { subagents: subagentNames(entries, resolve), addrs: addressNames(entries) };
  const boundToCard = new Map<string, PeerMessage[]>();
  const noticed = new Set<PeerMessage>();
  const replyEntry = new Map<PeerMessage, string>();
  const cards = new Map<string, PeerMessage>();
  const index = new Map<string, number>();
  const consumed = new Set<string>();
  const displaced = new Set<string>();
  const order: string[] = [];
  const notBefore = (e: PeerStreamEntry) => cardNotAfter(e, (id) => resolve?.cardTs?.(id));
  let lastHuman = -1;
  entries.forEach((e, i) => {
    if (isHumanTurn(e)) lastHuman = i;
    if (e.kind === "tool_call" && e.callId && e.name) {
      const msg = peerMessageFromToolCall({
        name: e.name,
        status: e.status ?? "",
        detail: e.detail,
        error: e.error,
      });
      if (msg) {
        const at = resolve?.cardTs?.(e.callId);
        cards.set(e.callId, {
          ...withDisplayName(msg, names, resolve),
          ...(at !== undefined ? { at } : {}),
        });
        index.set(e.callId, i);
        order.push(e.callId);
      }
      return;
    }
    if (e.kind !== "user_message" || typeof e.content !== "string") return;
    if (e.peerRelay?.kind === "notice" && e.content.startsWith(DELIVERY_NOTICE_PREFIX)) {
      // One row can hold several notices back to back: each settles its own card.
      const parts = noticeParts(e.content);
      const turnStart = entries.findLastIndex((x, j) => j < i && isHumanTurn(x));
      const isSub = (c: PeerMessage) =>
        names.subagents.has(c.target.id ?? c.target.name) || names.subagents.has(c.target.name);
      const settled = parts.map((p) => {
        const live = order.filter(notBefore(e));
        const awaiting = live
          .filter((id) => (index.get(id) ?? -1) > turnStart)
          .map((id) => cards.get(id))
          .filter(
            (c): c is PeerMessage =>
              !!c &&
              !c.reply &&
              c.target.kind === "session" &&
              (c.status === "sent" || c.status === "queued") &&
              !noticed.has(c) &&
              !isSub(c),
          );
        return settleFromNotice(p, cards, live, names.addrs, boundToCard, noticed, awaiting);
      });
      if (settled.length > 0 && settled.every(Boolean)) consumed.add(e.id);
      return;
    }
    const peer = incomingPeerOf(e);
    if (!peer) return;
    const cardOk = notBefore(e);
    const inTurn = turnGate(e, index, lastHuman);
    const eligible = (id: string) => cardOk(id) && inTurn(id);
    const ctx = { cards, order, notBefore: eligible, replyEntry, consumed, displaced, resolve };
    if (joinReply(e, peer, ctx)) consumed.add(e.id);
  });
  for (const c of cards.values()) expireLostHold(c, Date.now());
  if (resolve?.edges) {
    const { list, selfIds } = resolve.edges;
    for (const [id, edge] of joinEdgesToRows(cards, list, selfIds)) {
      const card = cards.get(id);
      if (card) applyEdge(card, edge, resolve);
    }
  }
  for (const c of cards.values()) hideUnresolvedId(c);
  // The relay refused THIS ask (no Pro, not a member …) and nothing answered it. A card with a reply is Replied.
  for (const c of cards.values()) {
    const why = c.askId && !c.reply ? resolve?.refusalOf?.(c.askId) : undefined;
    if (why) Object.assign(c, { status: "refused", reason: `Not delivered — ${why}` });
  }
  return { cards, consumed, displaced, index };
}

// ── "Talking to X…" and the strip rows ───────────────────────────────────────

const isUnansweredAsk = (c: PeerMessage): boolean =>
  !c.reply &&
  c.target.kind === "agent" &&
  (c.status === "sent" || c.status === "delivered" || c.status === "queued");

const isHumanTurn = (e: PeerStreamEntry): boolean =>
  e.kind === "user_message" && e.promptAuthor?.kind !== "agent";

/** Call ids pending in the CURRENT turn: in flight, or an unanswered ask while the turn is active. */
function pendingCardKeys(
  entries: readonly PeerStreamEntry[],
  turnActive: boolean,
  resolve?: PeerNameResolver,
): Set<string> {
  const { cards, index } = joinPeerMessages(entries, resolve);
  const turnStart = entries.findLastIndex(isHumanTurn);
  const keys = new Set<string>();
  for (const [key, c] of cards) {
    if (c.reply || (index.get(key) ?? -1) < turnStart) continue;
    const askAwaiting = turnActive && isUnansweredAsk(c);
    if (c.status === "sending" || askAwaiting) keys.add(key);
  }
  return keys;
}

export interface PendingPeer {
  name: string;
  kind: "session" | "agent";
}

export function pendingPeerNames(
  entries: readonly PeerStreamEntry[],
  turnActive: boolean,
  resolve?: PeerNameResolver,
): PendingPeer[] {
  const { cards } = joinPeerMessages(entries, resolve);
  const peers: PendingPeer[] = [];
  for (const key of pendingCardKeys(entries, turnActive, resolve)) {
    const t = cards.get(key)?.target;
    if (t?.name && !peers.some((p) => sameName(p.name, t.name)))
      peers.push({ name: t.name, kind: t.kind });
  }
  return peers;
}

export interface PeerStripRow {
  key: string;
  name: string;
  kind: "session" | "agent";
  /** The cybo id an ask targeted, when the tool carried one. */
  targetId?: string;
  /** A native SendMessage is not a question: "message". Only cyborg7_ask_agent is "asked". */
  label: "message" | "asked";
  text: string;
}

const STRIP_ROWS_MAX = 5;

/** The strip lists only what is pending now. Once sent, answered or failed the card in the timeline is the record. */
export function peerStripRows(
  entries: readonly PeerStreamEntry[],
  turnActive: boolean,
  resolve?: PeerNameResolver,
): PeerStripRow[] {
  const { cards } = joinPeerMessages(entries, resolve);
  const pending = pendingCardKeys(entries, turnActive, resolve);
  return [...cards]
    .filter(([key]) => pending.has(key))
    .slice(-STRIP_ROWS_MAX)
    .map(([key, c]) => ({
      key,
      name: c.target.name,
      kind: c.target.kind,
      targetId: c.target.id,
      label: c.target.kind === "agent" ? ("asked" as const) : ("message" as const),
      text: (c.summary ?? c.body).replace(/\s+/g, " ").trim(),
    }));
}

// ── who a peer is: cybo avatar, else provider icon ───────────────────────────

/** The agent-list fields the lookup reads (Agent row, #3734: peerName is the owner's own rows only). */
export interface PeerIdentityAgent {
  agentId?: string;
  provider?: string | null;
  cyboId?: string | null;
  cyboName?: string | null;
  cyboAvatar?: string | null;
  peerName?: string | null;
  // The session's shared name: a Claude session registers under it (dashes for spaces), so a
  // viewer whose row carries no peerName (not their session) still finds the row by it.
  sharedName?: string | null;
  daemonId?: string | null;
}

// Letters and digits only: "Release Notes" and its peer-name form "Release-Notes" compare equal.
const nameLetters = (s: string): string =>
  s
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");

/** Shaped for CyboSessionAvatar's `agent` prop: the one component the app draws agent avatars with. */
export interface PeerAvatarRow {
  provider?: string;
  cyboId?: string;
  cyboName?: string;
  cyboAvatar?: string;
}

// A Cyborg-spawned Claude session registers as `<alias | cybo slug | cwd name>-<first 8 of its session id>` (server
// claude-peer-name.ts derivePeerName). Not every client has the agent row's peerName (it is the owner's rows only, and a
// session on another daemon has none), so the suffix finds the row by session id. Exactly ONE row, never a guess.
const PEER_SUFFIX_RE = /^.+-([0-9a-f]{8})$/i;

export function agentOfPeerName<A extends PeerIdentityAgent>(
  raw: string,
  agents: readonly A[],
): A | undefined {
  const exact = agents.find((a) => !!a.agentId && a.agentId === raw);
  if (exact) return exact;
  const named = agents.find((a) => !!a.peerName && sameName(a.peerName, raw));
  if (named) return named;
  const letters = nameLetters(raw);
  const shared = agents.filter((a) => !!a.sharedName && nameLetters(a.sharedName) === letters);
  if (letters && shared.length === 1) return shared[0];
  const suffix = PEER_SUFFIX_RE.exec(raw.trim())?.[1].toLowerCase();
  if (!suffix) return undefined;
  const hits = agents.filter((a) => !!a.agentId && a.agentId.toLowerCase().startsWith(suffix));
  return hits.length === 1 ? hits[0] : undefined;
}

// A cybo's default DM session registers as `<cybo slug>-dmdef-cy` (its id `dmdef-<cyboId>-<user>`, first 8 chars): the slug finds the cybo.
const DMDEF_PEER_RE = /^(.+)-dmdef-cy$/i;

export function cyboOfPeerName<C extends { slug?: string }>(
  raw: string,
  cybos: readonly C[],
): C | undefined {
  const slug = DMDEF_PEER_RE.exec(raw.trim())?.[1].toLowerCase();
  return slug ? cybos.find((c) => c.slug?.toLowerCase() === slug) : undefined;
}

/** The one name every peer surface shows: cybo name, else alias, else the sidebar title; `<slug>-dmdef-cy` -> its cybo. */
export function peerDisplayName(
  raw: string,
  agents: readonly (PeerIdentityAgent & { agentId?: string })[],
  cybos: ReadonlyArray<{ id: string; name: string; slug: string }>,
  aliasOf: (agentId: string) => string | undefined,
): string | undefined {
  const agent = agentOfPeerName(raw, agents);
  if (agent)
    return (
      cyboNameOf(agent, cybos) ??
      (agent.agentId ? aliasOf(agent.agentId) : undefined) ??
      agentDisplayName(agent, cybos)
    );
  return cyboOfPeerName(raw, cybos)?.name ?? ephemeralPeerName(raw, cybos, agents);
}

// `<slug>-<hex8>` of a session whose own row is not listed: the cybo with that SLUG, only when a live agent row backs it
// (an agentId starting with the hex8, or a row of that cybo); else the slug humanized. Never the raw token.
function ephemeralPeerName(
  raw: string,
  cybos: ReadonlyArray<{ id: string; name: string; slug: string }>,
  agents: readonly PeerIdentityAgent[],
): string | undefined {
  const m = /^(.+)-([0-9a-f]{8})$/i.exec(raw.trim());
  if (!m) return undefined;
  const base = m[1].toLowerCase();
  const hex = m[2].toLowerCase();
  const hits = cybos.filter((c) => c.slug?.toLowerCase() === base);
  const backed = (c: { id: string }) =>
    agents.some((a) => a.agentId?.toLowerCase().startsWith(hex) || a.cyboId === c.id);
  if (hits.length === 1 && backed(hits[0])) return hits[0].name;
  return base.replace(/[-_]+/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const HOLD_TITLE_RE = /^(Held message from )(.+?)( \(name unverified\) · .*)$/;

/** Swaps the raw sender name in a hold title; keeps "(name unverified)", drops the socket address. */
export function peerHoldTitle(
  title: string,
  displayName: (raw: string) => string | undefined,
): string {
  const m = HOLD_TITLE_RE.exec(title);
  const name = m && displayName(m[2]);
  const out = m && name ? `${m[1]}${name}${m[3]}` : title;
  // The name only: the socket path is plumbing, never shown.
  return out.replace(/\s*·\s*\S*uds:.*$/, "").replace(/\S*uds:\S*/g, "an unidentified session");
}

function avatarOfCybo(
  agent: PeerIdentityAgent | undefined,
  cybos: ReadonlyArray<{ id: string; avatar?: string | null }>,
  cyboId: string,
): string | undefined {
  return agent?.cyboAvatar ?? cybos.find((c) => c.id === cyboId)?.avatar ?? undefined;
}

// An ask made with a cybo id never carries target.id, so the id the model passed is the name.
function findAskedCybo<C extends { id: string; name: string }>(
  peer: { name: string; id?: string },
  cybos: readonly C[],
): C | undefined {
  return cybos.find((c) => c.id === peer.id || c.id === peer.name || sameName(c.name, peer.name));
}

/**
 * cybo (an ask target by id/name, or a peer name an agent row registers) -> that session's
 * provider -> Claude for a session peer (native SendMessage is Claude's) -> undefined, and the
 * caller keeps its initials. `kind` undefined behaves like a session.
 */
const dmdefCyboOf = <C extends { slug?: string }>(
  peer: { name: string; id?: string },
  cybos: readonly C[],
): C | undefined =>
  cyboOfPeerName(peer.name, cybos) ?? (peer.id ? cyboOfPeerName(peer.id, cybos) : undefined);

export function resolvePeerAvatar(
  peer: { name: string; kind?: "session" | "agent"; id?: string },
  agents: readonly PeerIdentityAgent[],
  cybos: ReadonlyArray<{ id: string; name: string; slug?: string; avatar?: string | null }>,
): PeerAvatarRow | undefined {
  const cybo = peer.kind !== "agent" ? undefined : findAskedCybo(peer, cybos);
  // A card renamed by the resolver keeps the raw peer name in `id`.
  const agent =
    agentOfPeerName(peer.name, agents) ?? (peer.id ? agentOfPeerName(peer.id, agents) : undefined);
  const asked = cybo ?? (agent ? undefined : dmdefCyboOf(peer, cybos));
  const cyboId = asked?.id ?? agent?.cyboId ?? undefined;
  const provider = agent?.provider ?? (peer.kind === "agent" ? undefined : "claude");
  if (cyboId) {
    return {
      cyboId,
      cyboName: asked?.name ?? cyboNameOf(agent, cybos),
      cyboAvatar: asked?.avatar ?? avatarOfCybo(agent, cybos, cyboId),
      provider,
    };
  }
  return provider ? { provider } : undefined;
}

/** The one-line summary a collapsed incoming peer row shows: the body's first non-empty line, capped. */
export function peerPreview(body: string, max = 140): string {
  const line =
    body
      .split("\n")
      .map((l) => l.trim())
      .find(Boolean) ?? "";
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

export type PeerTypeChip = "Session" | "Agent" | "Terminal";

/** The agent row a peer is (by id, then by name); the one lookup the provider icon, type chip and machine chip share. */
export function peerAgentRow<A extends PeerIdentityAgent>(
  peer: { name: string; id?: string },
  agents: readonly A[],
): A | undefined {
  return (
    agentOfPeerName(peer.name, agents) ?? (peer.id ? agentOfPeerName(peer.id, agents) : undefined)
  );
}

/** An ask target is an Agent; a session Cyborg lists is a Session; one it does not list is a Claude Code Terminal outside Cyborg. */
const SUBAGENT_ID_RE = /^a[0-9a-f]{16}$/;

export function peerTypeChip(
  peer: { name: string; kind: "session" | "agent"; id?: string },
  agents: readonly PeerIdentityAgent[],
): PeerTypeChip {
  if (peer.kind === "agent") return "Agent";
  if (peerAgentRow(peer, agents)) return "Session";
  // An unlisted target is a Claude Code session outside Cyborg (Terminal), unless it is a raw subagent id (`a` + 16 hex).
  return SUBAGENT_ID_RE.test((peer.id ?? peer.name).trim()) ? "Agent" : "Terminal";
}
